import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { formaterFCFA } from '../../utils/montants';

/**
 * Lecture des demandes spéciales au Centre de Support.
 *
 * Le problème que ce fichier résout : le validateur voyait un dépotoir JSON. Les
 * deux types les plus anciens — salaire et profil employé — avaient chacun leur
 * composant sur mesure ; tous les autres tombaient dans un rendu générique qui
 * affichait `montant_mensuel_apres: 12000` à côté de `payload: {…}` sans
 * hiérarchie. Une demande se lit des jours après avoir été écrite, souvent par
 * quelqu'un qui n'a pas eu la conversation qui l'a précédée : elle doit se
 * comprendre sans décodage.
 *
 * Le choix de conception : un descripteur par type, pas un composant par type.
 * Dix composants sur mesure auraient dérivé les uns des autres — même carte
 * légèrement différente, même bloc de mois recopié — et le onzième type serait
 * retombé dans le JSON. Ici, ajouter un type se réduit à décrire ses champs.
 *
 * Les chemins pointent vers les champs RÉELLEMENT posés par les services. Un
 * chemin faux produit une carte vide, jamais une erreur : c'est volontaire, une
 * demande doit rester lisible même si un service change de forme.
 *
 * Le revers (relevé le 17/10/2026) : la création d'une famille pointait vers
 * des chemins qui n'ont jamais existé (famille.nom, membres, totaux.mensuel_*)
 * et le validateur n'en voyait que la phrase de lecture, sans un montant ni
 * l'avertissement de hausse. Les créations et les ajouts d'élève ont désormais
 * un tableau des mensualités (avant, après, écart), lu dans la forme écrite par
 * evaluateConstitution et evaluateAjout, demandes anciennes comprises.
 *
 * Suite du GO du 17/10/2026, trois défauts de lecture corrigés :
 * - un ajout sans mois libre dans l'année montrait un « après » qui ne
 *   s'applique à aucun mois (www n° 10 à 13) : l'écran le dit (effetAjout) ;
 * - une création ne montrait jamais l'avertissement d'éviction de la valeur de
 *   famille : le serveur l'écrit désormais (payload_demande
 *   .avertissement_eviction), les demandes anciennes n'en portent pas ;
 * - la phrase de lecture de l'ajout disait la part des autres membres
 *   « recomposée » sous accord : elle est reportée inchangée.
 */

// Une valeur absente s'écrit d'un tiret court (consigne de Daniel du 07/10/2026).
const VIDE = '-';

const formaterMontant = (valeur) => {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return VIDE;
  return formaterFCFA(nombre);
};

const lire = (objet, chemin) => chemin
  .split('.')
  .reduce((courant, cle) => (courant === null || courant === undefined ? undefined : courant[cle]), objet);

const formater = (valeur, format) => {
  if (valeur === null || valeur === undefined || valeur === '') return VIDE;
  if (format === 'montant') return formaterMontant(valeur);
  if (format === 'booleen') return valeur ? 'Oui' : 'Non';
  if (format === 'nombre') return new Intl.NumberFormat('fr-FR').format(Number(valeur) || 0);
  return String(valeur);
};

/**
 * Un montant tel que le serveur l'a écrit dans la demande : un nombre, ou un
 * texte « 13 000 FCFA » (formatFcfa des services de famille, toujours arrondi
 * au franc). Rend null pour tout le reste : un montant illisible n'est jamais
 * lu comme zéro ; il s'affiche tel quel et n'entre dans aucun écart.
 */
const lireMontant = (valeur) => {
  if (typeof valeur === 'number') return Number.isFinite(valeur) ? valeur : null;
  if (typeof valeur !== 'string') return null;
  const texte = valeur
    .replace(/\s*FCFA\s*$/i, '')
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(/\u2212/g, '-');
  if (/^-?\d+$/.test(texte)) return Number(texte);
  // « 13,000 » : un serveur privé des données de langue françaises sépare les
  // milliers par une virgule. Le franc CFA n'a pas de décimale.
  if (/^-?\d{1,3}([.,]\d{3})+$/.test(texte)) return Number(texte.replace(/[.,]/g, ''));
  return null;
};

/**
 * Les services de famille écrivent leurs libellés sans accents (« Appliquer la
 * reduction famille »). Ils sont rendus lisibles ici, mot à mot, sans rien
 * réécrire : la demande garde ce qu'elle a enregistré.
 */
const ACCENTS = [
  [/\bReduction\b/g, 'Réduction'],
  [/\breduction\b/g, 'réduction'],
  [/\bpersonnalisee\b/g, 'personnalisée'],
  [/\bsupplementaire\b/g, 'supplémentaire'],
  [/\bsupplement\b/g, 'supplément'],
  [/\bcumulee\b/g, 'cumulée'],
  [/\baccordee\b/g, 'accordée'],
  [/\bconservee\b/g, 'conservée'],
  [/\bretiree\b/g, 'retirée'],
  [/ a la /g, ' à la ']
];

const accentuer = (texte) => (typeof texte === 'string' && texte.trim()
  ? ACCENTS.reduce((courant, [motif, remplacement]) => courant.replace(motif, remplacement), texte.trim())
  : null);

const texteBrut = (valeur) => (typeof valeur === 'string' && valeur.trim() ? valeur.trim() : VIDE);

/**
 * Une ligne du tableau des mensualités : avant, après et écart, en nombres.
 * `sansEffet` : l'« après » ne s'applique à aucun mois de l'année ; l'écart ne
 * se lit alors ni comme une hausse ni comme une baisse.
 */
const ligneMensualite = ({ cle, nom, precisions = [], avant, apres, sansEffet = false }) => {
  const montantAvant = lireMontant(avant);
  const montantApres = lireMontant(apres);
  return {
    cle,
    nom: nom || VIDE,
    precisions: precisions.filter(Boolean),
    avant: montantAvant,
    apres: montantApres,
    avantTexte: montantAvant === null ? texteBrut(avant) : formaterFCFA(montantAvant),
    apresTexte: montantApres === null ? texteBrut(apres) : formaterFCFA(montantApres),
    ecart: montantAvant !== null && montantApres !== null ? montantApres - montantAvant : null,
    sansEffet
  };
};

const classeConnue = (classe) => (classe && classe !== '-' ? classe : null);

/**
 * Création d'une famille (FamilleConstitutionService.evaluateConstitution) :
 * la mensualité d'avant est dans payload_avant.situation, celle d'après dans
 * payload_demande.changements. Les deux listes sont écrites dans le même ordre,
 * un élève par ligne ; le nom départage si l'ordre ne correspond pas.
 */
const mensualitesCreation = (demande, avant) => {
  const situation = Array.isArray(avant.situation) ? avant.situation : [];
  const changements = Array.isArray(demande.changements) ? demande.changements : [];

  return changements.map((changement, index) => {
    const nom = changement?.eleve;
    const ligneAvant = (situation[index]?.eleve === nom
      ? situation[index]
      : situation.find((ligne) => ligne?.eleve === nom)) || {};
    const exceptionnelle = accentuer(changement?.reduction_exceptionnelle);

    return ligneMensualite({
      cle: `${index}-${nom}`,
      nom,
      precisions: [
        classeConnue(ligneAvant.classe),
        accentuer(changement?.decision),
        exceptionnelle ? `Réduction exceptionnelle : ${exceptionnelle}` : null
      ],
      avant: ligneAvant.mensuel_actuel,
      apres: changement?.mensuel_apres_validation
    });
  });
};

/**
 * Ajout d'un élève (FamilleAjoutEleveService.evaluateAjout) : l'élève ajouté
 * passe de ce qu'il paie seul (payload_avant.eleve_a_ajouter) à sa mensualité
 * dans la famille (eleve_ajoute.montant_effectif). Les membres déjà présents
 * sont lus avant et après l'ajout (membres_actuels, membres_apres_ajout).
 */
const mensualitesAjout = (demande, avant, effet) => {
  const ajoute = demande.eleve_ajoute || {};
  const membres = (Array.isArray(demande.membres_actuels) ? demande.membres_actuels : []).filter(Boolean);
  const apresAjout = new Map((Array.isArray(demande.membres_apres_ajout) ? demande.membres_apres_ajout : [])
    .filter(Boolean)
    .map((membre) => [String(membre.eleve_id), membre]));
  const lignes = [];

  if (ajoute.nom || ajoute.eleve_id) {
    lignes.push(ligneMensualite({
      cle: `ajoute-${ajoute.eleve_id}`,
      nom: ajoute.nom,
      precisions: [
        'Élève ajouté',
        classeConnue(ajoute.classe_nom),
        accentuer(demande.decision_reduction || ajoute.decision)
      ],
      avant: avant.eleve_a_ajouter?.montant_individuel_avant,
      apres: ajoute.montant_effectif,
      sansEffet: Boolean(effet?.aucunMois)
    }));
  }

  membres.forEach((membre) => {
    const apres = apresAjout.get(String(membre.eleve_id)) || membre;
    lignes.push(ligneMensualite({
      cle: `membre-${membre.eleve_id}`,
      nom: membre.nom,
      precisions: [classeConnue(membre.classe_nom)],
      avant: membre.montant_effectif,
      apres: apres.montant_effectif
    }));
  });

  return lignes;
};

/**
 * Le mois d'effet d'un ajout, et ce qu'il faut en dire quand l'année n'offre
 * aucun mois libre à l'élève ajouté. Dans le cas ordinaire, seulement
 * `{ mois }` : la carte lit mois_effet.nom, comme avant, et rien d'autre ne
 * s'affiche.
 *
 * Deux formes en base :
 * - `mois_effet.aucun_mois_libre` (buildMoisEffetStocke, depuis le 26/08) :
 *   l'exécution aligne les mois de l'élève à partir d'un ordre qui dépasse le
 *   dernier mois de l'année, c'est-à-dire aucun ;
 * - `mois_effet: null` (forme d'avant, www n° 10 à 13) : la demande promettait
 *   un rattachement « sans recalcul des lignes comptables existantes », et
 *   l'exécution de l'époque n'écrivait aucun mois sans mois d'effet.
 * Dans les deux cas, les autres membres gardent ce qu'ils paient.
 *
 * ⚠️ Les deux formes ne disent pas la même chose. `aucun_mois_libre` porte sur
 * l'élève ajouté (premierMoisLibre). Le `null` d'avant portait sur TOUTE la
 * famille : la requête de juillet (getMoisEffetAjout, c54b4fe) ne retenait
 * qu'un mois sans paiement commencé pour chaque membre à la fois, et l'élève
 * ajouté pouvait avoir des mois libres. L'avis le dit dans ses mots. Une
 * demande de forme ancienne qui n'est pas approuvée se lit comme avant :
 * exécutée aujourd'hui, elle partirait du premier mois libre de l'élève, que
 * l'écran ne connaît pas (aucune n'est en attente au relevé du 17/10).
 *
 * Une demande approuvée dit ce que l'exécution a fait : si son résultat porte
 * un mois (un mois s'était libéré entre la demande et la décision), c'est lui
 * qui fait foi, et l'écran ne prétend pas qu'aucun mois n'a bougé.
 */
// « à partir d'Octobre », « à partir de Mars » : l'élision devant une voyelle.
const aPartirDe = (mois) => (/^[aeiouyàâéèêîôû]/i.test(mois) ? `à partir d'${mois}` : `à partir de ${mois}`);

const effetAjout = (request, demande) => {
  const prevu = demande.mois_effet;
  const approuvee = request?.statut === 'approuvee';
  const formeAncienne = prevu === null;
  if (!((formeAncienne && approuvee) || prevu?.aucun_mois_libre === true)) return { mois: prevu?.nom };

  const nom = demande.eleve_ajoute?.nom || "L'élève ajouté";
  const resultat = approuvee ? request.payload_resultat : null;
  const applique = resultat && typeof resultat === 'object' ? resultat.mois_effet : null;
  const auDepot = formeAncienne
    ? "Au dépôt de la demande, aucun mois n'était libre pour toute la famille à la fois."
    : "Aucun mois de l'année n'était libre au dépôt de la demande.";

  if (applique && typeof applique === 'object' && applique.nom && !applique.aucun_mois_libre) {
    const mois = String(applique.nom);
    return {
      mois,
      aucunMois: false,
      titre: `Appliqué ${aPartirDe(mois)}`,
      texte: `${auDepot} À l'exécution, ${mois} l'était : `
        + `la mensualité « Après » de ${nom} s'applique à partir de ce mois.`
    };
  }

  // Le total famille « après » contient cette mensualité : il ne s'applique
  // pas davantage, l'avis le dit puisque sa carte reste affichée.
  return {
    mois: 'Aucun',
    aucunMois: true,
    titre: approuvee ? "Aucun mois n'a été modifié" : 'Aucun mois ne sera modifié',
    texte: `${auDepot} ${nom} rejoint la famille, mais sa mensualité « Après » ne s'applique à aucun mois `
      + "de cette année, pas plus que le total famille après : ses mois gardent leur montant, "
      + "et les autres membres gardent ce qu'ils paient."
  };
};

const TONS = {
  neutre: 'border-slate-200 bg-white text-slate-900',
  avant: 'border-slate-200 bg-slate-50 text-slate-900',
  apres: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  effet: 'border-orange-200 bg-orange-50 text-orange-900',
  alerte: 'border-amber-200 bg-amber-50 text-amber-900'
};

const LIBELLES_TON = {
  neutre: 'text-slate-500',
  avant: 'text-slate-500',
  apres: 'text-emerald-700',
  effet: 'text-orange-700',
  alerte: 'text-amber-700'
};

/**
 * Un descripteur par type. `lecture` est la phrase qui dit au validateur ce que
 * la demande fait et ce qu'elle ne fait pas — la partie qu'aucun JSON ne porte.
 */
const DESCRIPTEURS = {
  modification_reduction_eleve: {
    lecture: "Cette demande modifie la réduction mensuelle d'un élève, à partir du mois d'effet. "
      + 'Les mois déjà réglés ou entamés restent intacts. Les frais de début d\'année ne sont pas concernés : '
      + 'ils sont figés à l\'admission et disposent de leur propre demande.',
    cartes: [
      { label: 'Élève', chemin: 'eleve.nom' },
      { label: 'Classe', chemin: 'eleve.classe' },
      { label: 'Décision', chemin: 'decision_reduction' },
      { label: "Mois d'effet", chemin: 'mois_effet.nom', ton: 'effet' },
      { label: 'Payé aujourd\'hui', chemin: 'part_facturee_avant', format: 'montant', ton: 'avant' },
      { label: 'Payé après', chemin: 'part_facturee_apres', format: 'montant', ton: 'apres' },
      { label: 'Réduction demandée', chemin: 'reduction_demandee.totale' },
      { label: 'Avantage accord conservé', chemin: 'avantage_accord_conserve', format: 'montant', ton: 'alerte' }
    ],
    alertes: ['avertissement_eviction.message', 'avertissement_hausse.message'],
    mois: true,
    membres: { chemin: 'recomposition_accord.membres', titre: "Recomposition de l'accord financier" }
  },

  modification_frais_inscription: {
    lecture: "Cette demande corrige un frais de début d'année déjà enregistré. "
      + 'Elle ne touche à aucun mois de scolarité.',
    cartes: [
      { label: 'Élève', chemin: 'eleve.nom' },
      { label: 'Classe', chemin: 'eleve.classe' },
      { label: 'Type de frais', chemin: 'type_frais' },
      { label: 'Déjà encaissé', chemin: 'deja_encaisse', format: 'booleen', ton: 'alerte' },
      { label: 'Montant actuel', chemin: 'montant_avant', format: 'montant', ton: 'avant' },
      { label: 'Montant demandé', chemin: 'montant_apres', format: 'montant', ton: 'apres' },
      { label: 'Tarif de la classe', chemin: 'ecart_avec_tarif_classe' }
    ],
    note: 'note'
  },

  // Le taux horaire d'une classe (décisions de Daniel du 01/11/2026) : la
  // fiche d'un enseignant suit sa classe, et ce taux ne change que par ici.
  // L'ancien taux est relu à l'approbation : le résultat dit celui remplacé.
  modification_taux_horaire_classe: {
    lecture: "Cette demande change le taux horaire d'une classe, à partir du mois d'effet. Ce taux fait la paie "
      + "de tous les enseignants payés à l'heure de la classe : leur fiche suit la classe, rien n'y est à rattraper. "
      + "Les mois d'avant le mois d'effet, non figés, gardent l'ancien taux ; les mois figés ne bougent pas.",
    cartes: [
      { label: 'Classe', chemin: 'classe_nom' },
      { label: "Mois d'effet", chemin: 'mois_effet_nom', ton: 'effet' },
      { label: 'Taux actuel (FCFA / heure)', chemin: 'ancien_taux', format: 'montant', ton: 'avant' },
      { label: 'Taux demandé (FCFA / heure)', chemin: 'nouveau_taux', format: 'montant', ton: 'apres' },
      { label: "Enseignants payés à l'heure", chemin: 'enseignants_concernes', format: 'nombre' }
    ]
  },

  changement_classe_eleve: {
    lecture: "Cette demande change la classe d'un élève. Le tarif suit la nouvelle classe à partir "
      + "du mois d'effet ; les mois déjà engagés gardent leur montant. Les frais de début d'année "
      + 'suivent la classe sauf s\'ils sont déjà encaissés.',
    cartes: [
      { label: 'Élève', chemin: 'eleve.nom' },
      { label: 'Classe actuelle', chemin: 'classe_avant', ton: 'avant' },
      { label: 'Nouvelle classe', chemin: 'classe_apres', ton: 'apres' },
      { label: "Mois d'effet", chemin: 'mois_effet.nom', ton: 'effet' },
      { label: 'Tarif actuel', chemin: 'tarif_mensuel_avant', format: 'montant', ton: 'avant' },
      { label: 'Nouveau tarif', chemin: 'tarif_mensuel_apres', format: 'montant', ton: 'apres' },
      { label: 'Payé aujourd\'hui', chemin: 'part_facturee_avant', format: 'montant', ton: 'avant' },
      { label: 'Payé après', chemin: 'part_facturee_apres', format: 'montant', ton: 'apres' }
    ],
    mois: true
  },

  rattachement_eleves_famille_intersite: {
    lecture: "Cette demande rattache des élèves de ce site à une famille partagée avec une autre école. "
      + 'La valeur de réduction est pilotée par le site de référence. Les mois déjà réglés restent intacts.',
    cartes: [
      { label: 'Famille inter-sites', chemin: 'famille_nom' },
      { label: 'Site de référence', chemin: 'site_reference' },
      { label: 'Site concerné', chemin: 'site_subdomain' },
      { label: "Nombre d'élèves", chemin: 'eleve_ids.length', format: 'nombre' }
    ],
    eleves: { chemin: 'eleves', titre: 'Élèves à rattacher' }
  },

  desactivation_famille_intersite: {
    lecture: 'Cette demande sort une famille du groupe inter-sites. Les familles locales sont détachées ; '
      + 'celles qui tombent sous deux élèves sont archivées, les autres restent actives.',
    cartes: [
      { label: 'Famille', chemin: 'famille_nom' },
      { label: 'Site de référence', chemin: 'site_subdomain' },
      { label: 'Motif de sortie', chemin: 'motif' },
      { label: 'Précision', chemin: 'motif_detail' }
    ]
  },

  ajout_eleve_famille_nombreuse: {
    // Les autres membres ne sont pas recalculés : sans accord, rien n'est écrit
    // pour eux ; sous accord, un nouvel accord remplace l'ancien et y reporte
    // leur part négociée telle quelle (buildMemberState lit montant_effectif
    // dans le détail de l'accord actif, FamilleAccordService.recomposer
    // l'écrit). Ce n'était pas une « recomposition » de leur part.
    lecture: "Cette demande ajoute un élève à une famille nombreuse. Les autres membres gardent ce qu'ils "
      + 'paient : sous accord financier, leur part négociée est reportée inchangée dans le nouvel accord, '
      + "où seul l'élève ajouté entre avec sa mensualité. Les mois déjà réglés restent intacts.",
    cartes: [
      { label: 'Élève ajouté', chemin: 'eleve_ajoute.nom' },
      { label: 'Famille', chemin: 'famille.nom' },
      // « Aucun » quand l'année n'offre aucun mois libre (effetAjout).
      { label: "Mois d'effet", chemin: 'effet_affiche.mois', ton: 'effet' },
      { label: 'Total famille avant', chemin: 'ancien_total_famille', format: 'montant', ton: 'avant' },
      { label: 'Total famille après', chemin: 'nouveau_total_famille', format: 'montant', ton: 'apres' }
    ],
    effet: effetAjout,
    // L'éviction est posée sur l'élève ajouté (newState), pas à la racine.
    alertes: ['eleve_ajoute.avertissement_eviction.message', 'avertissement_hausse.message'],
    // Pas de ligne de total : les deux totaux famille sont dans les cartes, et
    // l'élève ajouté ne faisait pas partie de la famille avant.
    mensualites: {
      lire: mensualitesAjout,
      note: (effet) => (effet?.aucunMois
        ? "Avant : ce que l'élève ajouté paie aujourd'hui, seul. Après : sa mensualité dans la famille, "
          + "qui ne s'applique à aucun mois de cette année."
        : "Avant : ce que l'élève ajouté paie aujourd'hui, seul. Après : ce qu'il paiera dans la famille.")
    }
  },

  retrait_eleve_famille_nombreuse: {
    lecture: "Cette demande retire un élève d'une famille nombreuse. Il repasse au tarif de sa classe. "
      + "Si la famille tombe sous le minimum d'enfants, une régularisation s'ouvre.",
    cartes: [
      { label: 'Élève retiré', chemin: 'eleve_retire.nom' },
      { label: 'Famille', chemin: 'famille.nom' },
      { label: "Mois d'effet", chemin: 'mois_effet.nom', ton: 'effet' },
      { label: 'Total famille avant', chemin: 'ancien_total_famille', format: 'montant', ton: 'avant' },
      { label: 'Total famille après', chemin: 'nouveau_total_famille', format: 'montant', ton: 'apres' }
    ]
  },

  creation_famille_depuis_eleves_seuls: {
    lecture: "Cette demande constitue une famille nombreuse à partir d'élèves inscrits séparément. "
      + 'Chaque élève garde la règle de réduction choisie pour lui. Les paiements déjà faits en gestion '
      + 'individuelle restent intacts.',
    // Chemins de la forme réelle (evaluateConstitution) : la famille est dans
    // payload_demande.payload, le nombre d'élèves dans payload_avant. Les deux
    // réductions de la famille sont montrées : chacune suffit à exiger la
    // validation (referenceHasReduction), même si aucune mensualité ne bouge.
    cartes: [
      { label: 'Famille à créer', chemin: 'payload.nom' },
      { label: 'Responsable', chemin: 'payload.responsable_nom' },
      { label: "Nombre d'élèves", chemin: 'payload_avant.nombre_eleves', format: 'nombre' },
      { label: 'Réduction famille par mois', chemin: 'payload.reduction_mensuel', format: 'montant' },
      { label: "Réduction famille à l'inscription", chemin: 'payload.reduction_inscription', format: 'montant' }
    ],
    // L'éviction de la valeur de famille, écrite par evaluateConstitution
    // depuis la suite du GO du 17/10/2026 ; absente des demandes plus
    // anciennes, qui ne s'en trouvent pas changées.
    alertes: ['avertissement_eviction.message', 'avertissement_hausse.message'],
    mensualites: {
      lire: mensualitesCreation,
      total: true,
      note: "Avant : ce que chaque élève paie aujourd'hui. Après : ce qu'il paiera une fois la famille créée."
    }
  }
};

const Carte = ({ label, valeur, ton = 'neutre' }) => (
  <div className={`rounded-lg border p-4 ${TONS[ton] || TONS.neutre}`}>
    <div className={`text-[11px] font-semibold uppercase tracking-wide ${LIBELLES_TON[ton] || LIBELLES_TON.neutre}`}>
      {label}
    </div>
    <div className="mt-2 text-base font-semibold">{valeur}</div>
  </div>
);

const ListeMois = ({ titre, mois, ton }) => (
  <div className={`rounded-lg border p-4 ${TONS[ton]}`}>
    <div className={`text-[11px] font-semibold uppercase tracking-wide ${LIBELLES_TON[ton]}`}>{titre}</div>
    {mois.length === 0 ? (
      <p className="mt-2 text-sm text-slate-600">Aucun.</p>
    ) : (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {mois.map((ligne, index) => (
          <span
            key={`${ligne.mois || ligne.mois_nom || index}-${index}`}
            className="rounded border border-white/60 bg-white px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {ligne.mois || ligne.mois_nom || VIDE}
            {ligne.nouveau_montant !== undefined ? ` · ${formaterMontant(ligne.nouveau_montant)}` : ''}
          </span>
        ))}
      </div>
    )}
  </div>
);

/**
 * L'écart d'une mensualité : une hausse se voit, une baisse aussi, le reste se
 * tait. Une mensualité qui ne s'applique à aucun mois n'a pas d'écart à lire.
 */
const TexteEcart = ({ ecart, sansEffet = false }) => {
  if (sansEffet) return <span className="text-slate-500">Aucun mois</span>;
  if (ecart === null) return <span className="text-slate-400">{VIDE}</span>;
  if (ecart === 0) return <span className="text-slate-500">Inchangé</span>;
  return ecart > 0
    ? <span className="font-semibold text-amber-700">+{formaterFCFA(ecart)}</span>
    : <span className="font-semibold text-emerald-700">-{formaterFCFA(-ecart)}</span>;
};

/**
 * Ce que chaque élève paie par mois avant l'opération, après, et l'écart. C'est
 * ce que le validateur approuve : il le lit ici, sans décodage.
 */
const TableauMensualites = ({ lignes, total, note }) => {
  const lisibles = lignes.every((ligne) => ligne.avant !== null && ligne.apres !== null);
  const totalAvant = lignes.reduce((somme, ligne) => somme + (ligne.avant || 0), 0);
  const totalApres = lignes.reduce((somme, ligne) => somme + (ligne.apres || 0), 0);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="px-4 pt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        Mensualités avant et après
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
              <th scope="col" className="px-4 py-2 font-medium">Élève</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">Avant</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">Après</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">Écart</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((ligne) => (
              <tr
                key={ligne.cle}
                className={`border-b border-slate-100 last:border-b-0 ${ligne.ecart > 0 && !ligne.sansEffet ? 'bg-amber-50' : ''}`}
              >
                <td className="px-4 py-2.5 align-top">
                  <div className="font-semibold text-slate-800">{ligne.nom}</div>
                  {ligne.precisions.length > 0 && (
                    <div className="mt-0.5 text-xs text-slate-500">{ligne.precisions.join(' · ')}</div>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right align-top text-slate-700">{ligne.avantTexte}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right align-top font-semibold text-slate-900">
                  {ligne.apresTexte}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right align-top">
                  <TexteEcart ecart={ligne.ecart} sansEffet={ligne.sansEffet} />
                </td>
              </tr>
            ))}
          </tbody>
          {/* Le total n'est posé que si chaque montant a été lu : une somme
              qui sauterait une ligne illisible mentirait. */}
          {total && lisibles && (
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50">
                <th scope="row" className="px-4 py-2.5 text-left font-semibold text-slate-800">Total</th>
                <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold text-slate-700">
                  {formaterFCFA(totalAvant)}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold text-slate-900">
                  {formaterFCFA(totalApres)}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right">
                  <TexteEcart ecart={totalApres - totalAvant} />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {note && <p className="border-t border-slate-100 px-4 py-2.5 text-[11px] text-slate-500">{note}</p>}
    </div>
  );
};

export function aUnDescripteur(typeDemande) {
  return Boolean(DESCRIPTEURS[typeDemande]);
}

export default function DetailsDemandeStructuree({ request }) {
  const descripteur = DESCRIPTEURS[request?.type_demande];
  if (!descripteur) return null;

  const demande = request?.payload_demande || {};
  const avant = request?.payload_avant || {};

  // Ce que l'écran déduit du mois d'effet (ajout d'un élève), lu par la carte
  // « Mois d'effet », l'avis sous les cartes et le tableau.
  const effet = descripteur.effet ? descripteur.effet(request, demande) : null;

  // Le contexte de lecture d'un chemin : la demande d'abord, l'avant ensuite.
  // Certains champs utiles ne vivent que dans payload_avant.
  const contexte = { ...demande, payload_avant: avant, effet_affiche: effet };

  const cartes = (descripteur.cartes || [])
    .map((carte) => {
      const brut = carte.chemin.endsWith('.length')
        ? (lire(contexte, carte.chemin.replace(/\.length$/, '')) || []).length
        : lire(contexte, carte.chemin);
      return { ...carte, valeur: formater(brut, carte.format) };
    })
    // Une carte vide n'apprend rien : on ne l'affiche pas plutôt que d'aligner
    // des tirets. Le descripteur peut donc décrire des champs facultatifs.
    .filter((carte) => carte.valeur !== VIDE);

  const alertes = (descripteur.alertes || [])
    .map((chemin) => lire(contexte, chemin))
    .filter(Boolean);

  const moisRecalcules = Array.isArray(demande.mois_recalcules) ? demande.mois_recalcules : [];
  const moisConserves = Array.isArray(demande.mois_conserves) ? demande.mois_conserves : [];
  const membres = descripteur.membres ? lire(contexte, descripteur.membres.chemin) : null;
  const eleves = descripteur.eleves ? lire(contexte, descripteur.eleves.chemin) : null;
  const note = descripteur.note ? lire(contexte, descripteur.note) : null;
  const mensualites = descripteur.mensualites ? descripteur.mensualites.lire(demande, avant, effet) : [];
  const noteMensualites = typeof descripteur.mensualites?.note === 'function'
    ? descripteur.mensualites.note(effet)
    : descripteur.mensualites?.note;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#246990]" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#246990]">
              Lecture de la demande
            </div>
            <p className="mt-1 text-sm leading-6 text-slate-700">{descripteur.lecture}</p>
          </div>
        </div>
      </div>

      {cartes.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {cartes.map((carte) => (
            <Carte key={carte.label} label={carte.label} valeur={carte.valeur} ton={carte.ton} />
          ))}
        </div>
      )}

      {effet?.titre && (
        <div className={`rounded-lg border p-4 ${TONS.effet}`}>
          <div className={`text-[11px] font-semibold uppercase tracking-wide ${LIBELLES_TON.effet}`}>{effet.titre}</div>
          <p className="mt-1 text-sm leading-6">{effet.texte}</p>
        </div>
      )}

      {alertes.map((message, index) => (
        <div key={index} className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <p className="text-sm leading-6 text-amber-900">{message}</p>
          </div>
        </div>
      ))}

      {mensualites.length > 0 && (
        <TableauMensualites
          lignes={mensualites}
          total={Boolean(descripteur.mensualites.total)}
          note={noteMensualites}
        />
      )}

      {note && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
          {note}
        </div>
      )}

      {descripteur.mois && (moisRecalcules.length > 0 || moisConserves.length > 0) && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ListeMois titre="Mois recalculés" mois={moisRecalcules} ton="apres" />
          <ListeMois titre="Mois conservés" mois={moisConserves} ton="avant" />
        </div>
      )}

      {Array.isArray(eleves) && eleves.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {descripteur.eleves.titre}
          </div>
          <div className="mt-2 space-y-1.5">
            {eleves.map((eleve, index) => (
              <div
                key={eleve.eleve_id || index}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
              >
                <span className="font-semibold text-slate-800">{eleve.nom || `Élève #${eleve.eleve_id}`}</span>
                <span className="text-xs text-slate-500">
                  {[eleve.classe, eleve.mode_reduction, eleve.frais_mensuel !== undefined
                    ? formaterMontant(eleve.frais_mensuel)
                    : null].filter(Boolean).join(' · ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(membres) && membres.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
            {descripteur.membres.titre}
          </div>
          <p className="mt-1 text-xs text-amber-800">
            Sous accord, le montant facturé vient de la part négociée. Seul l'élève concerné est recalculé ;
            les autres gardent la leur.
          </p>
          <div className="mt-2 space-y-1.5">
            {membres.map((membre, index) => (
              <div
                key={`${membre.eleve}-${index}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-100 bg-white px-3 py-2 text-sm"
              >
                <span className="font-semibold text-slate-800">
                  {membre.eleve}
                  {membre.recalcule && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                      recalculé
                    </span>
                  )}
                </span>
                <span className="text-xs font-semibold text-slate-700">{membre.part}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
